/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Collapse, ListItemButton, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { Button, Divider, List, ListItem, colors, makeStyles, Typography, Grid } from '@material-ui/core';
import { DashboardHelpers } from 'utils';
import _ from 'lodash';
import { useOrganizations, useProperties, useFields, useDevices } from 'hooks';

const useStyles = makeStyles(() => ({
  greyBackground: { backgroundColor: colors.grey[100] },
  infoKeyBold: { fontWeight: 'bold', fontSize: '14px' },
  infoValue: { fontSize: '14px' },
}));

const CollapsibleListRow = ({ data, tableHeader, viewItem }) => {
  const { t } = useTranslation();
  const { organizations } = useOrganizations();
  const { fields } = useFields();
  const { properties } = useProperties();
  const { devices } = useDevices();
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const infoRowText = (key, obj) => {
    const value = obj[key];
    if (key === 'layer_id') {
      let layerName = '';
      if (obj.layer === 'device') {
        const device = _.find(devices, (device) => {
          const simpleHwAddress = device.hw_address?.replaceAll(':', '');
          return simpleHwAddress === obj.device_id || simpleHwAddress === obj.related;
        });

        if (device?.name) {
          layerName = device.name;
        } else if (device?.hw_address) {
          layerName = DashboardHelpers.trimHWAddress(device.hw_address);
        } else if (obj.device_id) {
          layerName = DashboardHelpers.trimHWAddress(obj.device_id);
        } else if (obj.related) {
          layerName = DashboardHelpers.trimHWAddress(obj.related);
        } else {
          layerName = t('unknown');
        }
      } else {
        layerName = DashboardHelpers.getObjectById(obj.layer_id, organizations, properties, fields)?.name;
      }

      return (
        <Grid container item>
          <Typography className={classes.infoKeyBold}>{t(key)}</Typography>
          <Typography className={classes.infoValue}>{': ' + layerName}</Typography>
        </Grid>
      );
    }
    return (
      <Grid container item>
        <Typography className={classes.infoKeyBold}>{t(key)}</Typography>
        <Typography className={classes.infoValue}>{': ' + DashboardHelpers.formatDateTime(value)}</Typography>
      </Grid>
    );
  };

  const previewItems = _.filter(tableHeader, { mobilePreview: true }).map((item) => item.dataAlias);
  const preview = previewItems.map((item) => {
    const flattenedData = DashboardHelpers.flattenObject(data);
    return <ListItemText key={item} primary={DashboardHelpers.formatDateTime(flattenedData[item])} />;
  });

  const infoItems = _.filter(tableHeader, { mobileInfo: true }).map((item) => item.dataAlias);
  const info = infoItems.map((item) => {
    const flattenedData = DashboardHelpers.flattenObject(data);
    return (
      <ListItem key={item}>
        <ListItemText
          className={classes.textStyles}
          disableTypography
          key={item}
          primary={infoRowText(item, flattenedData)}
        />
      </ListItem>
    );
  });

  return (
    <div>
      <ListItemButton key={data.id} onClick={handleClick}>
        {preview}
        <Button onClick={viewItem}>{t('view')}</Button>
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider />
        <List className={classes.greyBackground}>{info}</List>
        <Divider />
      </Collapse>
    </div>
  );
};

CollapsibleListRow.propTypes = {
  data: PropTypes.object,
  tableHeader: PropTypes.arrayOf(
    PropTypes.shape({
      alias: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      sortable: PropTypes.bool.isRequired,
      mobilePreview: PropTypes.bool,
      mobileInfo: PropTypes.bool,
      format: PropTypes.oneOf(['date', 'string', 'number']),
      dataAlias: PropTypes.string.isRequired,
      className: PropTypes.string,
      dataRender: PropTypes.func,
    })
  ).isRequired,
  viewItem: PropTypes.func.isRequired,
};

CollapsibleListRow.defaultProps = {
  data: undefined,
  tableHeader: undefined,
};

export default CollapsibleListRow;
